import {
  Stack,
  Text,
  Button,
  Group,
  Checkbox,
  Skeleton,
  Pagination,
} from '@mantine/core';
import BigNumber from 'bignumber.js';
import { useState } from 'react';
import cn from 'clsx';
import { WalletSuspense } from 'src/components/WalletSuspense/WalletSuspense';
import { ErrorDisplay } from 'src/components/ErrorDisplay/ErrorDisplay';
import { Rows } from 'src/components/Rows/Rows';
import { FilterButtons } from 'src/components/FilterButtons/FilterButtons';
import { useOracleInfo, useWalletRequests } from 'src/api';
import {
  useStakingIsPaused,
  useWithdrawalDelay,
} from 'src/api/smart-contracts';
import { formatDecimal } from 'src/utils';
import {
  SHOW_GAS_FEES,
  ALWAYS_SHOW_STAKING_STATUS_BANNER,
} from 'src/constants';
import { ClaimRequest } from './ClaimRequest';
import { useRedeem } from './useRedeem';

export function ClaimPanel() {
  return (
    <Stack gap={24}>
      <WalletSuspense
        fallback={
          <Text ta="center">
            Connect wallet to see your withdrawal requests.
          </Text>
        }
      >
        <RequestsList />
      </WalletSuspense>

      <WalletSuspense buttonProps={{ size: 'lg' }} />
    </Stack>
  );
}

const FILTER_ITEMS = [
  { label: 'All', value: 'all' },
  { label: 'Ready to claim', value: 'claimable' },
  { label: 'Pending', value: 'pending' },
];

const REQUESTS_PER_PAGE = Number(
  import.meta.env.VITE_WALLET_REQUESTS_PER_PAGE || 20
);

function RequestsList() {
  const [page, setPage] = useState(1);

  const { oracleInfo, isLoadingOracleInfo, oracleInfoError } = useOracleInfo();

  const { withdrawalDelay, isLoadingWithdrawalDelay, withdrawalDelayError } =
    useWithdrawalDelay();

  const { walletRequests, isLoadingWalletRequests, walletRequestsError } =
    useWalletRequests();

  const isLoading =
    isLoadingOracleInfo || isLoadingWithdrawalDelay || isLoadingWalletRequests;
  const error = oracleInfoError || withdrawalDelayError || walletRequestsError;

  const [activeFilter, setActiveFilter] = useState<
    (typeof FILTER_ITEMS)[number]['value']
  >(FILTER_ITEMS[0].value);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [redeemIds, setRedeemIds] = useState<string[]>([]);
  const [redeemedIds, setRedeemedIds] = useState<string[]>([]);

  const { stakingIsPaused, stakingIsPausedMessage } = useStakingIsPaused();

  const claimTotalAmount = selectedIds.reduce((acc, id) => {
    const request = walletRequests.find((r) => r.id === id);
    return acc.plus(request ? request.assets : 0);
  }, BigNumber(0));

  const {
    transactionCost,
    isLoadingTransactionCost,
    isRedeeming,
    redeemError,
    redeem,
  } = useRedeem({
    requestIds: selectedIds,
    onSuccess: (hash?: string) => {
      setRedeemedIds([...redeemedIds, ...redeemIds]);
      setRedeemIds([]);
      setSelectedIds([]);
    },
  });

  const claimableRequests = walletRequests.filter(
    (r) => r.is_claimable && !r.claimed && !redeemedIds.includes(r.id)
  );
  const pendingRequests = walletRequests.filter(
    (r) =>
      !r.claimed &&
      !r.is_claimable &&
      !redeemedIds.includes(r.id) &&
      !redeemIds.includes(r.id)
  );
  const unclaimedRequests = walletRequests.filter(
    (r) => !r.claimed || redeemedIds.includes(r.id)
  );

  const allSelected = selectedIds.length === claimableRequests.length;

  const filteredRequests =
    activeFilter === 'claimable'
      ? claimableRequests
      : activeFilter === 'pending'
        ? pendingRequests
        : unclaimedRequests;

  const footerItems = [
    ...(SHOW_GAS_FEES
      ? [
          {
            label: 'Estimated transaction cost',
            value: isLoadingTransactionCost ? (
              <Skeleton w={160} h={23} />
            ) : !selectedIds.length ? (
              '-'
            ) : (
              <>{formatDecimal(transactionCost, { withTruncate: true })} MON</>
            ),
          },
        ]
      : []),
  ];

  const submitDisabled =
    stakingIsPaused || isRedeeming || !redeem || !selectedIds.length;

  const handleSubmit = () => {
    if (submitDisabled) {
      return;
    }

    setRedeemIds(selectedIds);
    redeem();
  };

  const renderRequests = () => {
    if (isLoading) {
      return (
        <Text my={80} ta="center">
          Loading...
        </Text>
      );
    }

    if (error && !ALWAYS_SHOW_STAKING_STATUS_BANNER) {
      return (
        <Stack>
          <Text ta="center">
            Oops, something went wrong. Try refreshing the page.
          </Text>
          <ErrorDisplay>{error?.message}</ErrorDisplay>
        </Stack>
      );
    }

    if (!filteredRequests.length) {
      return (
        <Text ta="center">
          No{' '}
          {activeFilter === 'claimable'
            ? 'claimable'
            : activeFilter === 'pending'
              ? 'pending'
              : ''}{' '}
          requests found.
        </Text>
      );
    }

    const pagesTotal = Math.ceil(filteredRequests.length / REQUESTS_PER_PAGE);

    return (
      <>
        {filteredRequests
          .slice((page - 1) * REQUESTS_PER_PAGE, page * REQUESTS_PER_PAGE)
          .map((request) => {
            const isChecked = selectedIds.includes(request.id);

            return (
              <ClaimRequest
                key={request.id}
                oracleInfo={oracleInfo}
                withdrawalDelay={withdrawalDelay}
                request={request}
                isChecked={isChecked}
                isRedeeming={isRedeeming && redeemIds.includes(request.id)}
                isRedeemed={redeemedIds.includes(request.id)}
                onCheck={() =>
                  setSelectedIds(
                    isChecked
                      ? selectedIds.filter((id) => id !== request.id)
                      : [...selectedIds, request.id]
                  )
                }
              />
            );
          })}
        {pagesTotal > 1 && (
          <Pagination
            mx="auto"
            total={pagesTotal}
            value={page}
            onChange={setPage}
          />
        )}
      </>
    );
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
      className={cn((stakingIsPaused || isRedeeming) && 'pointer-events-none')}
    >
      <Stack gap={24}>
        {!!unclaimedRequests.length && (
          <Group justify="space-between">
            <Checkbox
              label={
                allSelected && !!claimableRequests.length
                  ? 'Unselect All'
                  : 'Select All'
              }
              checked={!!claimableRequests.length && allSelected}
              size="md"
              fz={16}
              c="#7B7B7B"
              disabled={!claimableRequests.length || activeFilter === 'pending'}
              classNames={{ label: 'pl-2' }}
              onChange={() =>
                allSelected
                  ? setSelectedIds([])
                  : setSelectedIds(claimableRequests.map((r) => r.id))
              }
            />

            <FilterButtons
              data={FILTER_ITEMS}
              value={activeFilter}
              selectMaxW="lg"
              onChange={(newActiveFilter) => {
                setActiveFilter(newActiveFilter);
                setPage(1);
              }}
            />
          </Group>
        )}

        <Stack gap={24} className={cn(isRedeeming && 'pointer-events-none')}>
          <Stack gap={8}>{renderRequests()}</Stack>

          <Stack gap={16}>
            <Button
              variant="gradient"
              size="lg"
              type="submit"
              disabled={submitDisabled}
              loading={isRedeeming}
            >
              {stakingIsPaused ? (
                stakingIsPausedMessage
              ) : (
                <>
                  Claim{' '}
                  {formatDecimal(claimTotalAmount.div(1e18).toFixed(), {
                    withTruncate: true,
                  })}{' '}
                  MON
                </>
              )}
            </Button>

            <ErrorDisplay>{redeemError?.message}</ErrorDisplay>

            {!!footerItems.length && (
              <Stack gap={8}>
                <Rows items={footerItems} />
              </Stack>
            )}
          </Stack>
        </Stack>
      </Stack>
    </form>
  );
}
